import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9osataqs38sj6jc7ej0';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [serverMsg, setServerMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setServerMsg('Thank you for your message! We will get back to you soon.');
      return;
    }

    setStatus('sending');
    setServerMsg('');

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');

      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }

      if (res.ok && parsed && parsed.code === 'OK') {
        setStatus('success');
        setServerMsg('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        const msg = (parsed as Record<string, unknown> | null)?.meta as Record<string, unknown> | undefined;
        const detail = typeof msg?.message === 'string' ? msg.message : (typeof msg?.detail === 'string' ? msg.detail : '');
        if (detail && String(detail).includes('spam')) {
          setStatus('error');
          setServerMsg('Submission could not be processed. Please try again.');
        } else {
          setStatus('error');
          setServerMsg(detail || 'Something went wrong. Please try again later.');
        }
      }
    } catch {
      setStatus('error');
      setServerMsg('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="relative w-full py-16 md:py-20 bg-background-50">
      <div className="max-w-2xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
            Get in Touch
          </h2>
          <p className="text-foreground-600 text-sm">
            Have questions about Sugargoo Spreadsheet? Send us a message.
          </p>
        </div>

        <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="hp-field"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Name
              </label>
              <input
                id="contact-name"
                name="name"
                type="text"
                required
                className="w-full px-4 py-3 rounded-xl border border-background-300/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                placeholder="Your name"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Email
              </label>
              <input
                id="contact-email"
                name="email"
                type="email"
                required
                className="w-full px-4 py-3 rounded-xl border border-background-300/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Subject
            </label>
            <input
              id="contact-subject"
              name="subject"
              type="text"
              required
              className="w-full px-4 py-3 rounded-xl border border-background-300/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all"
              placeholder="What is this about?"
            />
          </div>

          <div>
            <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Message
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              maxLength={500}
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-background-300/70 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent transition-all resize-none"
              placeholder="Your message (max 500 characters)"
            ></textarea>
          </div>

          <button
            type="submit"
            disabled={status === 'sending'}
            className="w-full py-3.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all whitespace-nowrap cursor-pointer"
          >
            {status === 'sending' ? 'Sending...' : 'Send Message'}
          </button>

          {status === 'success' && (
            <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-200 text-secondary-700 text-sm text-center">
              {serverMsg}
            </div>
          )}
          {status === 'error' && (
            <div className="p-4 rounded-xl bg-primary-50 border border-primary-200 text-primary-700 text-sm text-center">
              {serverMsg}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}